import Vue from "vue";
import LevelService from "@/services/LevelService";
import BuildingService from "@/services/BuildingService";

export default {
  namespaced: true,
  state: () => ({
    /* Core building data*/
    buildingId: null,
    isEdit: false,
    isBoundarySet: false,
    /* Level Management */
    allLevelObjects: {},
    files: [],
    isUploadingFile: false,
    referenceLevelId: null,
    levelToAlignId: null,
    newReferenceLevelToBe: undefined,
    isAlignmentEditMode: false,
    /* Processing states */
    isFineTuning: false,
    isFineTuned: false,
    resultGeojsons: {},
    floorPlanFileState: {}, // Track file processing status by fid
    fineTuneFeatures: [],
    // Panel & UI state that affects multiple components*/
    defaultOpenPanelIndex: 0,
    shouldSkip: {
      1: false,
      2: false,
      3: false
    },
    uploadProgress: {}, // Track upload progress by fid
    levelStatuses: null,
    aiMappingLevelTypes: ["office", "hotel", "hospital", "university"],
    aiMappingFileTypes: ["pdf", "dxf", "dwg"],
    isInitializing: false,
    lastEditedLevelIndex: null,
    isFineTuneEditMode: false,
    shouldDisableReferenceChange: false
  }),

  mutations: {
    SET_BUILDING_ID(state, buildingId) {
      state.buildingId = buildingId;
    },
    ADD_LEVEL_OBJECTS(state, payload) {
      let newLevelObjects;
      let buildingId;

      // Handle both formats for backward compatibility
      if (Array.isArray(payload)) {
        newLevelObjects = payload;
        buildingId = state.buildingId;
      } else {
        newLevelObjects = payload.newLevelObjects;
        buildingId = payload.buildingId || state.buildingId;
      }

      // Instead of returning, make sure we have a valid building ID
      buildingId = buildingId || state.buildingId;

      if (!buildingId) {
        console.error("No buildingId available for ADD_LEVEL_OBJECTS");
        return;
      }

      if (!state.allLevelObjects[buildingId]) {
        Vue.set(state.allLevelObjects, buildingId, []); // Use Vue.set for reactivity
      }

      const updatedLevelObjects = [...state.allLevelObjects[buildingId], ...newLevelObjects];
      Vue.set(state.allLevelObjects, buildingId, updatedLevelObjects); // Use Vue.set for reactivity
    },
    UPDATE_LEVEL_OBJECT_AT_INDEX(state, { index, updates }) {
      if (!state.allLevelObjects[state.buildingId]) {
        state.allLevelObjects[state.buildingId] = [];
      }
      const currentLevel = state.allLevelObjects[state.buildingId][index];
      if (!currentLevel) {
        return;
      }
      // Create a new object with updated properties to ensure reactivity
      const updatedLevel = {
        ...currentLevel,
        ...updates
      };
      // Replace the entire array to ensure getters that use .find() detect the change
      const updatedArray = [...state.allLevelObjects[state.buildingId]];
      updatedArray[index] = updatedLevel;
      Vue.set(state.allLevelObjects, state.buildingId, updatedArray);
    },
    SET_LEVEL_OBJECTS(state, payload) {
      const { levelObjects, buildingId } = payload;
      const buildingIdToSet = buildingId || state.buildingId;
      Vue.set(state.allLevelObjects, buildingIdToSet, levelObjects);
    },

    SET_FILES(state, files) {
      state.files = files;
    },
    IS_UPLOADING_FILE(state, isUploadingFile) {
      state.isUploadingFile = isUploadingFile;
    },

    UPDATE_LEVEL_OBJECT(state, { fid, updates }) {
      if (!state.allLevelObjects[state.buildingId]) {
        state.allLevelObjects[state.buildingId] = [];
      }
      const index = state.allLevelObjects[state.buildingId].findIndex((level) => level.fid === fid);
      if (index !== -1) {
        const currentLevel = state.allLevelObjects[state.buildingId][index];
        // Create a new object with updated properties to ensure reactivity
        const updatedLevel = {
          ...currentLevel,
          ...updates
        };
        // Replace the entire array to ensure getters that use .find() detect the change
        const updatedArray = [...state.allLevelObjects[state.buildingId]];
        updatedArray[index] = updatedLevel;
        Vue.set(state.allLevelObjects, state.buildingId, updatedArray);
      }
    },
    CLEAR_LEVEL_OBJECT_FEATURES(state, { levelIndex }) {
      if (!state.allLevelObjects[state.buildingId]) {
        state.allLevelObjects[state.buildingId] = [];
      }
      const index = state.allLevelObjects[state.buildingId].findIndex(
        (level) => Number(level.levelIndex) === Number(levelIndex)
      );
      if (index === -1) {
        return;
      }
      const levelObject = state.allLevelObjects[state.buildingId][index];
      const updatedLevel = {
        ...levelObject,
        preview: {
          ...levelObject.preview,
          geojson: {
            ...(levelObject.preview?.geojson || { type: "FeatureCollection" }),
            features: []
          }
        }
      };
      const updatedArray = [...state.allLevelObjects[state.buildingId]];
      updatedArray[index] = updatedLevel;
      Vue.set(state.allLevelObjects, state.buildingId, updatedArray);

      if (levelObject.fid === state.referenceLevelId) {
        state.fineTuneFeatures = [];
      }
    },

    UPDATE_LEVEL_PREVIEW(state, { fid, preview }) {
      if (!state.allLevelObjects[state.buildingId]) {
        state.allLevelObjects[state.buildingId] = [];
      }
      const index = state.allLevelObjects[state.buildingId].findIndex((level) => level.fid === fid);
      if (index !== -1) {
        // Use Vue.set to reactively update just the preview property
        // of the specific level object at the found index
        Vue.set(state.allLevelObjects[state.buildingId][index], "preview", preview);
      }
    },

    SET_REFERENCE_LEVEL(state, levelId) {
      if (!levelId) {
        state.referenceLevelId = null;
        state.allLevelObjects[state.buildingId] = [];
        state.newReferenceLevelToBe = undefined;
        state.fineTuneFeatures = [];
        return;
      }

      // Remove alignment parameters from all levels and reset alignment status
      if (!state.allLevelObjects[state.buildingId]) {
        state.allLevelObjects[state.buildingId] = [];
      }

      state.allLevelObjects[state.buildingId] = state.allLevelObjects[state.buildingId].map((lvl) => {
        const newPreview = { ...lvl.preview, alignmentParameters: undefined };
        return {
          ...lvl,
          alignmentStatus: lvl.fid === levelId ? "reference" : "initial",
          preview: newPreview
        };
      });

      state.referenceLevelId = levelId;
      state.newReferenceLevelToBe = undefined;

      const level = state.allLevelObjects[state.buildingId].find((level) => level.fid === levelId);
      state.fineTuneFeatures = level?.preview?.geojson?.features || [];
    },

    SET_LEVEL_TO_ALIGN(state, levelId) {
      state.levelToAlignId = levelId;
    },

    SET_NEW_REFERENCE_LEVEL(state, level) {
      state.newReferenceLevelToBe = level;
    },

    RESET_ALIGNMENT_PARAMETERS(state) {
      if (!state.allLevelObjects[state.buildingId]) {
        state.allLevelObjects[state.buildingId] = [];
      }
      state.allLevelObjects[state.buildingId] = state.allLevelObjects[state.buildingId].map((level) => ({
        ...level,
        preview: level.preview?.alignmentParameters
          ? {
              ...level.preview,
              alignmentParameters: undefined
            }
          : level.preview,
        alignmentStatus: level.fid === state.referenceLevelId ? "reference" : "initial"
      }));
    },

    SET_IS_FINE_TUNING(state, isFineTuning) {
      state.isFineTuning = isFineTuning;
    },

    SET_IS_FINE_TUNED(state, isFineTuned) {
      state.isFineTuned = isFineTuned;
    },

    SET_IS_FINE_TUNE_EDIT_MODE(state, isFineTuneEditMode) {
      state.isFineTuneEditMode = isFineTuneEditMode;
    },

    FLOOR_PLAN_FILE_STATE(state, { fid, isLoading }) {
      Vue.set(state.floorPlanFileState, fid, isLoading);
    },

    ADD_FILES(state, newFiles) {
      state.files = [...state.files, ...newFiles];
    },

    ADD_FILE(state, newFile) {
      state.files = [newFile, ...state.files];
    },

    UPDATE_FILE_AT_INDEX(state, { index, file }) {
      Vue.set(state.files, index, file);
    },

    REMOVE_FILE_AT_INDEX(state, index) {
      const updatedFiles = [...state.files];
      updatedFiles.splice(index, 1);
      state.files = updatedFiles;
    },
    SET_FINE_TUNE_FEATURES(state, features) {
      state.fineTuneFeatures = features;
    },
    SET_IS_BOUNDARY_SET(state, payload) {
      const { isBoundarySet, buildingId } = payload;

      if (state.buildingId !== buildingId) {
        return;
      }
      // Set boundary flag at the state level for current building
      state.isBoundarySet = isBoundarySet;
    },
    SET_SHOULD_SKIP(state, { index, value }) {
      Vue.set(state.shouldSkip, index, value);
      if (index === 1) {
        Vue.set(state.shouldSkip, 2, value);
        Vue.set(state.shouldSkip, 3, value);
      } else if (index === 2) {
        Vue.set(state.shouldSkip, 3, value);
      }
    },
    UPDATE_UPLOAD_PROGRESS(state, { fid, progress }) {
      Vue.set(state.uploadProgress, fid, progress);
    },
    SET_LEVEL_STATUSES(state, levelStatuses) {
      state.levelStatuses = levelStatuses;
    },
    SET_IS_ALIGNMENT_EDIT_MODE(state, isAlignmentEditMode) {
      state.isAlignmentEditMode = isAlignmentEditMode;
    },
    SET_SHOULD_DISABLE_REFERENCE_CHANGE(state, shouldDisableReferenceChange) {
      state.shouldDisableReferenceChange = shouldDisableReferenceChange;
    },
    SET_IS_EDIT_MODE(state, isEditMode) {
      state.isEdit = isEditMode;
    }
  },

  actions: {
    async REMOVE_LEVEL({ commit, state }, index) {
      if (!state.allLevelObjects[state.buildingId]) {
        state.allLevelObjects[state.buildingId] = [];
      }
      const temp = [...state.allLevelObjects[state.buildingId]];
      const removed = temp.splice(index, 1);
      // Use Vue.set to ensure reactivity when updating arrays
      Vue.set(state.allLevelObjects, state.buildingId, [...temp]);
      if (removed[0].fid === state.referenceLevelId) {
        commit("RESET_ALIGNMENT_PARAMETERS");
        const newReferenceLevel = state.allLevelObjects[state.buildingId].find((level) => level.fid !== removed[0].fid);
        commit("SET_REFERENCE_LEVEL", newReferenceLevel?.fid);
        commit("SET_IS_FINE_TUNED", false);

        if (temp.length > 1) {
          const newLevelToAlign = temp.find((level) => level.fid !== newReferenceLevel.fid);
          commit("SET_LEVEL_TO_ALIGN", newLevelToAlign?.fid);
        }
      } else if (removed[0].fid === state.levelToAlignId) {
        const newLevelToAlign = state.allLevelObjects[state.buildingId].find(
          (level) => level.fid !== removed[0].fid && level.fid !== state.referenceLevelId
        );
        commit("SET_LEVEL_TO_ALIGN", newLevelToAlign?.fid);
      }
    },

    async INITIALIZE_LEVELS(
      { commit, state, getters, rootState, rootGetters },
      { isReadOnly, clientInternalIdentifier, abortSignal }
    ) {
      if (!rootState.MAP.currentBuilding) {
        return;
      }
      state.isInitializing = true;
      const buildingObject = rootGetters["MAP/currentBuildingObject"];
      const referenceLevelId = buildingObject?.properties?.alignmentConfigs?.referenceLevel;
      const previewPropsOfLevels = buildingObject?.properties?.alignmentConfigs?.previewProps;
      const { currentClient: clientId, currentSite: siteId, currentBuilding: buildingId } = rootState.MAP;
      const buildingIDToInitialize = buildingId;
      try {
        // Check if aborted before starting
        if (abortSignal?.aborted) {
          throw new DOMException("Operation aborted", "AbortError");
        }
        const levels = rootState.CONTENT.levels;
        const geojson = await BuildingService.getAllDraftFeaturesOfBuilding(clientId, siteId, buildingId, abortSignal);

        if (!geojson?.features) {
          throw new Error("No geojson found");
        }

        let files = [];
        const lastSavedInitialAnchors = buildingObject?.properties?.alignmentConfigs?.initialAnchors;
        const lastSavedUpdatedAnchors = buildingObject?.properties?.alignmentConfigs?.updatedAnchors;
        // Use Promise.all to wait for all async operations to complete
        const levelObjects = await Promise.all(
          levels.map(async (level, index) => {
            // Check if aborted before processing each level
            if (abortSignal?.aborted) {
              throw new DOMException("Operation aborted", "AbortError");
            }
            let levelFeatures = geojson.features.filter((feature) => feature.properties.lvl == level.properties.lvl);
            const levelId = level.properties.fid;
            let file;
            let alignmentParameters;
            let alignmentStatus;

            file = new File([], level?.properties?.extra?.fileName || "");
            files.push(file);

            alignmentStatus = buildingObject?.properties?.alignmentConfigs?.alignmentState?.[levelId] || "initial";
            alignmentParameters = buildingObject?.properties?.alignmentConfigs?.alignmentParameters?.[levelId];
            if (alignmentStatus !== "reference" && alignmentParameters === undefined) {
              alignmentStatus = "initial";
            }
            if (referenceLevelId === levelId) {
              alignmentStatus = "reference";
            }
            alignmentParameters = buildingObject?.properties?.alignmentConfigs?.alignmentParameters?.[levelId];
            let isMapscaleResultReceived =
              buildingObject?.properties?.alignmentConfigs?.receivedMapscaleResult?.[levelId];

            let initialAnchors = lastSavedInitialAnchors?.[levelId];
            if (!initialAnchors) {
              initialAnchors = [level?.properties?.extra?.initialAnchorA, level?.properties?.extra?.initialAnchorB];
            }
            let updatedAnchors = lastSavedUpdatedAnchors?.[levelId];
            let resultGeojson;
            if (levelFeatures.length === 0) {
              const { fileType } = level.properties.extra;
              let urlToFetch =
                fileType === "json" || fileType === "geojson"
                  ? level?.properties?.extra?.fileUrl
                  : level?.properties?.extra?.geojsonUrl;
              const response = await fetch(urlToFetch, { signal: abortSignal });
              resultGeojson = await response.json();
              levelFeatures = resultGeojson.features.map((feature) => {
                if (!feature.properties.typeCode) {
                  feature.properties.typeCode = "undefined";
                }
                return feature;
              });
              initialAnchors = resultGeojson.features.filter(
                (feature) => feature.properties.typeCode === "georeferencing-anchor"
              );
              isMapscaleResultReceived = false;
            }

            const levelGeoJson = {
              type: "FeatureCollection",
              features: [...levelFeatures]
            };
            const { previewUrl, previewSize, previewAnchors } = previewPropsOfLevels?.[levelId] || {};
            // Check if we need to generate preview properties for this level
            let previewProps = {
              url: previewUrl || level?.properties?.extra?.previewUrl,
              size: previewSize || level?.properties?.extra?.previewSize,
              geojson: levelGeoJson,
              geojsonUrl: level?.properties?.extra?.geojsonUrl,
              anchors: previewAnchors || level?.properties?.extra?.previewAnchors,
              alignmentParameters: alignmentParameters,
              initialAnchorA: initialAnchors?.initialAnchorA || initialAnchors[0] || null,
              initialAnchorB: initialAnchors?.initialAnchorB || initialAnchors[1] || null,
              isMapscaleResultReceived: isMapscaleResultReceived,
              isTemp: level?.properties?.isTemp
            };
            if (updatedAnchors?.updatedAnchorA && updatedAnchors?.updatedAnchorB) {
              previewProps.updatedAnchorA = updatedAnchors.updatedAnchorA;
              previewProps.updatedAnchorB = updatedAnchors?.updatedAnchorB;
            }
            // If the level is missing key preview properties, generate them using geojson-to-png
            if (!previewProps.url || !previewProps.size || !previewProps.anchors) {
              try {
                const pngResponse = await LevelService.geojsonToPng(
                  clientInternalIdentifier,
                  {
                    geojson: levelGeoJson
                  },
                  abortSignal
                );
                if (pngResponse?.outputFileUrl) {
                  previewProps.url = pngResponse.outputFileUrl;
                  previewProps.size = pngResponse.imageSize;
                  previewProps.anchors = pngResponse.imageGeoPairs;
                  const geometry = level.geometry;
                  const properties = {
                    ...level.properties,
                    extra: {
                      ...level.properties.extra,
                      previewUrl: previewProps.url,
                      previewSize: previewProps.size,
                      previewAnchors: previewProps.anchors
                    }
                  };
                  if (!isReadOnly) {
                    LevelService.updateLevel(
                      rootState.MAP.currentClient,
                      rootState.MAP.currentSite,
                      rootState.MAP.currentBuilding,
                      level.properties.lvl,
                      {
                        type: "FeatureCollection",
                        features: [
                          {
                            type: "Feature",
                            properties,
                            geometry
                          }
                        ]
                      },
                      abortSignal
                    );
                  }
                }
              } catch (error) {
                console.error("Error generating preview for level:", error);
              }
            }

            // Ensure we have anchor features
            if ((!previewProps.initialAnchorA || !previewProps.initialAnchorB) && levelFeatures.length > 0) {
              try {
                const anchorResponse = await LevelService.calculateAnchors(
                  clientInternalIdentifier,
                  {
                    geojson: levelGeoJson
                  },
                  abortSignal
                );
                if (anchorResponse?.features?.length >= 2) {
                  previewProps.initialAnchorA = anchorResponse.features[0];
                  previewProps.initialAnchorB = anchorResponse.features[1];

                  // Add the anchors to the level features if they don't exist
                  const anchorFeatures = levelFeatures.filter(
                    (feature) => feature.properties.typeCode === "georeferencing-anchor"
                  );

                  if (anchorFeatures.length === 0) {
                    previewProps.geojson.features.push(...anchorResponse.features);
                  }
                }
              } catch (error) {
                console.error("Error calculating anchors for level:", error);
              }
            }

            return {
              fid: levelId,
              levelIndex: level?.properties?.lvl,
              levelShortTitle: level?.properties?.shortName,
              levelLongTitle: level?.properties?.name,
              fileUrl: level?.properties?.extra?.fileUrl,
              fileName: level?.properties?.extra?.fileName,
              levelType: level?.properties?.type || level?.properties?.levelType,
              preview: previewProps,
              fileType: level?.properties?.extra?.fileType || "geojson",
              isConfirmed: true,
              alignmentStatus: referenceLevelId === undefined && index === 0 ? "reference" : alignmentStatus,
              bid: buildingId,
              isDefault: !!level?.properties?.isDefault
            };
          })
        );

        commit("SET_LEVEL_OBJECTS", { levelObjects, buildingId: buildingIDToInitialize });
        if (state.buildingId !== buildingIDToInitialize) {
          return;
        }
        commit(
          "SET_IS_FINE_TUNED",
          (!getters.isFloorPlanAlignmentNeeded || getters.isAllAligned) &&
            buildingObject?.properties?.alignmentConfigs?.isFineTuned
        );
        commit("SET_SHOULD_DISABLE_REFERENCE_CHANGE", !!buildingObject?.properties?.alignmentConfigs?.isFineTuned);

        // Set reference level if exists
        const referenceLevel = levelObjects.find((level) => level?.fid === referenceLevelId);
        if (referenceLevel) {
          state.referenceLevelId = referenceLevel.fid;
        } else {
          state.referenceLevelId = levelObjects[0]?.fid;
          // Set the alignment status of the first level to "reference"
          if (levelObjects[0]) {
            commit("UPDATE_LEVEL_OBJECT", {
              fid: levelObjects[0].fid,
              updates: {
                alignmentStatus: "reference"
              }
            });
          }
        }

        commit("SET_FILES", files);
      } catch (error) {
        console.error("Error initializing levels:", error);
        throw error;
      } finally {
        state.isInitializing = false;
      }
    },

    async CHANGE_REFERENCE_LEVEL({ commit, state, getters }, { newValue }) {
      const oldValue = state.referenceLevelId;
      // 1. Skip if it's initial load in edit mode
      if (state.isEdit && !oldValue) {
        return;
      }

      try {
        // 2. Reset alignment parameters and update isAligned flags
        commit("RESET_ALIGNMENT_PARAMETERS");

        // 3. Handle levelToAlign swap if needed
        if (newValue === state.levelToAlignId) {
          const newLevelToAlign = getters.levelObjects.find((levelObject) => levelObject.fid === oldValue);
          //
          commit("SET_LEVEL_TO_ALIGN", newLevelToAlign.fid);
        }

        // Finally, update the reference level in state
        commit("SET_REFERENCE_LEVEL", newValue);
      } catch (error) {
        console.error("Error changing reference level:", error);
        throw error;
      }
    },
    async CONFIRM_ALIGNMENT({ commit, state, getters }, { alignmentParameters }) {
      const levelObject = getters.levelObjects.find((l) => l.fid === state.levelToAlignId);
      commit("UPDATE_LEVEL_OBJECT", {
        fid: state.levelToAlignId,
        updates: {
          preview: {
            ...levelObject?.preview,
            alignmentParameters
          },
          alignmentStatus: "parametersSet"
        }
      });
    },

    async PERFORM_FINE_TUNING({ commit, getters }, payload) {
      const {
        alignedGeojsons,
        sourceCoordinatesForlevelsThatAreRealigned,
        targetCoordinatesForlevelsThatAreRealigned,
        sourceAGeoCoordinates,
        sourceBGeoCoordinates,
        targetAGeoCoordinates,
        targetBGeoCoordinates,
        clientId
      } = payload;

      await Promise.all(
        Object.entries(alignedGeojsons).map(async ([fid, geojson]) => {
          const sourceCoordinatesFromRealignment = sourceCoordinatesForlevelsThatAreRealigned[fid];
          const targetCoordinatesFromRealignment = targetCoordinatesForlevelsThatAreRealigned[fid];
          const payload = {
            sourceAGeoCoordinates: sourceCoordinatesFromRealignment
              ? sourceCoordinatesFromRealignment.sourceA //geotransformdan dönen 2 nokta
              : sourceAGeoCoordinates,
            sourceBGeoCoordinates: sourceCoordinatesFromRealignment
              ? sourceCoordinatesFromRealignment.sourceB
              : sourceBGeoCoordinates,
            targetAGeoCoordinates: targetCoordinatesFromRealignment
              ? targetCoordinatesFromRealignment.targetA
              : targetAGeoCoordinates,
            targetBGeoCoordinates: targetCoordinatesFromRealignment
              ? targetCoordinatesFromRealignment.targetB
              : targetBGeoCoordinates,
            geoJson: geojson
          };

          const finalGeojson = await LevelService.geoTransform(clientId, payload);

          // Update the preview.geojson of the level object
          commit("UPDATE_LEVEL_OBJECT", {
            fid,
            updates: {
              preview: {
                ...getters.levelObjects.find((level) => level.fid === fid).preview,
                geojson: finalGeojson
              },
              alignmentStatus: "aligned"
            }
          });

          return [fid, finalGeojson];
        })
      );

      commit("SET_IS_FINE_TUNING", false);
      commit("SET_IS_FINE_TUNED", true);
      commit("SET_SHOULD_DISABLE_REFERENCE_CHANGE", true);
    },
    async ADD_FILES({ commit }, files, index) {
      commit("ADD_FILES", files, index);
    },
    async ADD_FILE({ commit }, newFile) {
      commit("ADD_FILE", newFile);
    },
    async UPDATE_FILE({ commit }, { index, file }) {
      commit("UPDATE_FILE_AT_INDEX", { index, file });
    },
    async REMOVE_FILE({ commit }, index) {
      commit("REMOVE_FILE_AT_INDEX", index);
    },
    async RESET_STORE({ state }) {
      state.buildingId = null;
      state.isEdit = false;
      state.isBoundarySet = false;
      state.allLevelObjects = {};
      state.files = [];
      state.referenceLevelId = null;
      state.levelToAlignId = null;
      state.newReferenceLevelToBe = undefined;
      state.isFineTuning = false;
      state.isFineTuned = false;
      state.resultGeojsons = {};
      state.floorPlanFileState = {};
      state.fineTuneFeatures = [];
      state.defaultOpenPanelIndex = 0;
      state.shouldSkip = {
        1: false,
        2: false,
        3: false
      };
      state.isUploadingFile = false;
      state.uploadProgress = {};
      state.levelStatuses = null;
      state.isFineTuneEditMode = false;
      state.isAlignmentEditMode = false;
      state.isInitializing = false;
      state.shouldDisableReferenceChange = false;
    },
    async FETCH_LEVEL_STATUSES({ commit, state, getters }, payload) {
      if (!payload.clientId || !payload.siteId || !payload.buildingId) {
        console.debug("Could not set level statuses - invalid params");
        return;
      }
      try {
        const response = await LevelService.getLevelStatuses(payload?.clientId, payload?.siteId, payload?.buildingId);
        let levelStatusesDict = {};
        if (response.message) {
          console.error(response.message);
          return;
        }
        // Group statuses by levelIndex
        const statusesByLevel = {};
        const levelFids = getters.levelObjects.map((level) => level.fid);
        let currentStatuses = response.filter((levelStatus) => levelFids.includes(levelStatus.levelIdentifier));
        currentStatuses?.forEach((levelStatus) => {
          if (!statusesByLevel[levelStatus.levelIndex]) {
            statusesByLevel[levelStatus.levelIndex] = [];
          }
          statusesByLevel[levelStatus.levelIndex].push(levelStatus);
        });

        // For each level, find the status with the most recent createdDate
        for (const [levelIndex, statuses] of Object.entries(statusesByLevel)) {
          const mostRecentStatus = statuses.reduce((latest, current) => {
            const latestDate = new Date(latest.createdDate);
            const currentDate = new Date(current.createdDate);
            return currentDate > latestDate ? current : latest;
          });
          levelStatusesDict[levelIndex] = mostRecentStatus;

          // For successful statuses, check alignment parameters and fetch results if needed
          if (mostRecentStatus.status === "Success") {
            const levelObject = getters.levelObjects.find((level) => level.levelIndex == levelIndex);
            if (levelObject && !levelObject.preview?.isMapscaleResultReceived) {
              try {
                const response = await fetch(mostRecentStatus.resultUrl);
                const resultGeojson = await response.json();
                const pngResponse = await LevelService.geojsonToPng(payload.clientId, {
                  geojson: resultGeojson
                });

                if (!pngResponse?.outputFileUrl) {
                  throw new Error("Invalid PNG conversion response");
                }

                //TODO: This is a temporary fix to get the anchors after mapscale
                const anchorsAfterMapscale = resultGeojson.features.filter(
                  (feature) =>
                    feature.properties.typeCode === "georeferencing-anchor" &&
                    !feature.properties.isInitialA &&
                    !feature.properties.isInitialB
                );
                // ignore, if any, updatedAnchorA and updatedAnchorB after mapscale
                const { updatedAnchorA, updatedAnchorB, ...previewWithoutUpdatedAnchors } = levelObject.preview;
                console.debug(updatedAnchorA, updatedAnchorB);
                const isTempLevel = levelObject?.preview?.isTemp;
                const isEditLevel = state.isEdit;
                let features;
                if (isTempLevel) {
                  features = resultGeojson.features || [];
                } else if (isEditLevel) {
                  const isAllFeaturesUndefined = levelObject.preview?.geojson?.features
                    ?.filter((feature) => feature.properties.typeCode !== "georeferencing-anchor")
                    ?.every((feature) => feature.properties.typeCode === "undefined");
                  if (levelObject.preview?.geojson?.features?.length > 0 && !isAllFeaturesUndefined) {
                    features = levelObject.preview?.geojson?.features;
                  } else {
                    features = resultGeojson.features;
                  }
                } else {
                  features = resultGeojson.features || [];
                }
                commit("UPDATE_LEVEL_OBJECT", {
                  fid: levelObject.fid,
                  updates: {
                    preview: {
                      ...previewWithoutUpdatedAnchors,
                      geojson: {
                        type: "FeatureCollection",
                        features: features
                      },
                      isMapscaleResultReceived: true,
                      initialAnchorA: anchorsAfterMapscale[0],
                      initialAnchorB: anchorsAfterMapscale[1],
                      url: pngResponse.outputFileUrl,
                      size: pngResponse.imageSize,
                      anchors: pngResponse.imageGeoPairs
                    }
                  }
                });
                if (levelObject.fid === state.referenceLevelId) {
                  state.fineTuneFeatures = features;
                }
              } catch (error) {
                console.error(`Error fetching results for level ${levelIndex}:`, error);
              }
            }
          }
        }

        commit("SET_LEVEL_STATUSES", levelStatusesDict);
      } catch (error) {
        console.error(error);
      }
    },
    async REMOVE_LEVEL_OBJECT_FEATURES_BY_LEVEL_INDEX({ commit }, { levelIndex }) {
      commit("CLEAR_LEVEL_OBJECT_FEATURES", { levelIndex });
    }
  },

  getters: {
    sortedLevelObjects: (state, getters) => {
      return [...getters.levelObjects].sort((a, b) => a.levelIndex - b.levelIndex);
    },

    isAllAligned: (state, getters) => {
      return (
        (getters.levelObjects.length > 0 &&
          getters.levelObjects.every(
            (level) =>
              level.alignmentStatus === "aligned" ||
              level.alignmentStatus === "parametersSet" ||
              level.alignmentStatus === "reference"
          )) ||
        getters.levelObjects.length === 1
      );
    },

    referenceLevel: (state, getters) => {
      if (!state.referenceLevelId) return null;
      return getters.levelObjects.find((level) => level.fid === state.referenceLevelId);
    },

    levelToAlign: (state, getters) => {
      if (!state.levelToAlignId) return null;
      return getters.levelObjects.find((level) => level.fid === state.levelToAlignId);
    },

    referencePlan: (state, getters) => {
      if (!state.referenceLevelId) return null;
      return getters.referenceLevel?.preview;
    },

    planToAlign: (state, getters) => {
      if (!state.levelToAlignId) return null;
      return getters.levelToAlign?.preview;
    },

    isFloorPlanAlignmentNeeded: (state, getters) => {
      return getters.levelObjects.length > 1;
    },

    isAllConfirmed: (state, getters) => {
      return getters.levelObjects.every((level) => level.isConfirmed === true);
    },

    alignedCount: (state, getters) => {
      return getters.levelObjects.filter(
        (level) => level.alignmentStatus === "aligned" || level.alignmentStatus === "parametersSet"
      ).length;
    },
    processedLevelStatuses: (state, getters) => {
      if (!state.levelStatuses) return null;
      const statusMapping = {
        Success: { label: "completed", color: "#28CC7A" },
        Failure: { label: "error", color: "#E43458" },
        InProgress: { label: "inprogress", color: "#42A5D7" },
        NotApplicable: { label: "notApplicable", color: "#808080" },
        Initial: { label: "initial", color: "#0D44C2" },
        Declined: { label: "error", color: "#E43458" },
        Cancelled: { label: "cancelled", color: "#E43458" },
        Received: { label: "inprogress", color: "#42A5D7" }
      };

      const counts = {
        fileError: { value: 0, label: "fileError", color: "#E43458", opacity: 0.7 },
        missingInformation: { value: 0, label: "missingInformation", color: "#fab735", opacity: 0.7 },
        completed: { value: 0, label: "completed", color: "#28CC7A", opacity: 0.7 },
        error: { value: 0, label: "error", color: "#E43458", opacity: 0.7 },
        inprogress: { value: 0, label: "inprogress", color: "#42A5D7", opacity: 0.7 },
        notApplicable: { value: 0, label: "notApplicable", color: "#808080", opacity: 0.7 },
        initial: { value: 0, label: "initial", color: "#0D44C2", opacity: 0.7 }
      };

      getters.levelObjects.forEach((level) => {
        const fileType = level.fileType?.toLowerCase();
        const levelType = level.levelType?.toLowerCase();
        const status = state.levelStatuses[level.levelIndex]?.status;
        if (level.fileError) {
          counts.fileError.value++;
        } else if (!level.isConfirmed) {
          counts.missingInformation.value++;
        } else if (status) {
          const mappedStatus = statusMapping[status]?.label;
          if (counts[mappedStatus]) {
            counts[mappedStatus].value++;
          } else if (mappedStatus === "cancelled") {
            counts.initial.value++;
          }
        } else if (fileType === "json" || fileType === "geojson") {
          counts.notApplicable.value++;
        } else if (
          (fileType === "dxf" || fileType === "dwg" || fileType === "pdf") &&
          state.aiMappingLevelTypes.includes(levelType)
        ) {
          counts.initial.value++;
        }
      });
      return Object.values(counts).filter((section) => section.value > 0);
    },
    mapScaleData: (state) => {
      if (!state.levelStatuses) return null;
      const levelStatuses = Object.values(state.levelStatuses);
      const successLevelStatuses = levelStatuses.filter((status) => status.status === "Success");
      const totalMappedArea = successLevelStatuses.reduce((sum, status) => sum + status.resultExtra.areaSqFeet, 0);
      let maxDuration = 0;
      successLevelStatuses.forEach((status) => {
        const createdDate = new Date(status.createdDate);
        const finishedDate = new Date(status.finishedDate);
        const timeDiff = finishedDate.getTime() - createdDate.getTime();
        const timeDiffInMinutes = Number((timeDiff / (1000 * 60)).toFixed(2));
        maxDuration = Math.max(maxDuration, timeDiffInMinutes);
      });

      return {
        totalMappedArea,
        duration: maxDuration
      };
    },
    isLevelStepsCompleted: (state, getters) => {
      return {
        1: getters.levelObjects.length > 0 && !getters.isMapscaleInProgress && getters.isAllConfirmed,
        2:
          getters.levelObjects.length > 1 &&
          getters.levelObjects.every(
            (level) =>
              level.alignmentStatus === "aligned" ||
              level.alignmentStatus === "parametersSet" ||
              level.alignmentStatus === "reference"
          ),
        3: state.isFineTuned
      };
    },
    isMapscaleInProgress: (state, getters) => {
      return getters.levelObjects.some(
        (level) =>
          (state.aiMappingFileTypes.includes(level.fileType) &&
            state.aiMappingLevelTypes.includes(level.levelType) &&
            state.levelStatuses?.[level.levelIndex]?.status === "Received") ||
          state.levelStatuses?.[level.levelIndex]?.status === "InProgress"
      );
    },
    isThereAMapscaleError: (state, getters) => {
      if (!state.levelStatuses) return null;
      return getters.levelObjects.some(
        (level) =>
          state.levelStatuses[level.levelIndex]?.status === "Failure" ||
          state.levelStatuses[level.levelIndex]?.status === "Declined"
      );
    },
    levelObjects: (state) => {
      return state.allLevelObjects[state.buildingId] || [];
    },
    isMapscaleApplicable: (state, getters) => {
      return getters.levelObjects.some(
        (level) =>
          state.aiMappingFileTypes.includes(level.fileType) && state.aiMappingLevelTypes.includes(level.levelType)
      );
    }
  }
};
